import React, { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { updateFavoriteSpotHabitat } from "../../features/favoriteSpots/favoriteSpotHabitatApi";
import {
  emptyFishingSetting,
  hasFishingSetting,
  normalizeFishingSetting,
} from "../../features/fishingIntelligence/fishingSetting";
import SpeciesFishingSetting from "../fishingToday/SpeciesFishingSetting";
import MarineFishingPlatform from "../fishingToday/MarineFishingPlatform";
import { explicitMarinePlatform } from "../../features/fishingIntelligence/marineFishingContext";
import "../../styles/components/FavoriteSpotFishingSetting.css";

const settingsEqual = (left, right) =>
  left.accessContext === right.accessContext &&
  left.habitatFeatures.join("|") === right.habitatFeatures.join("|");

export default function FavoriteSpotFishingSetting({ spot, onSaved }) {
  const { t } = useTranslation();
  const reactId = useId();
  const idPrefix = `favorite-spot-setting-${reactId.replace(/:/g, "")}`;
  const savedSetting = useMemo(
    () => normalizeFishingSetting(spot?.habitatContext || emptyFishingSetting()),
    [spot?.habitatContext]
  );
  const [draft, setDraft] = useState(savedSetting);
  const savedPlatform = explicitMarinePlatform(spot?.habitatContext?.platform);
  const [platform, setPlatform] = useState(savedPlatform);
  const [platformEdited, setPlatformEdited] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const isDirty = !settingsEqual(draft, savedSetting) || platform !== savedPlatform;
  const hasSavedSetting = hasFishingSetting(savedSetting);

  useEffect(() => {
    setDraft(savedSetting);
    setPlatform(savedPlatform);
    setPlatformEdited(false);
    setNotice("");
    setError("");
  }, [savedSetting, savedPlatform, spot?.spotKey]);

  const save = async (setting) => {
    if (!spot?.spotKey || isSaving) return;
    setIsSaving(true);
    setNotice("");
    setError("");
    try {
      const normalized = normalizeFishingSetting(setting);
      const response = await updateFavoriteSpotHabitat({
        ...(platformEdited ? { platform } : {}),
        spotKey: spot.spotKey,
        habitatFeatures: normalized.habitatFeatures,
        accessContext: normalized.accessContext,
      });
      const updatedSpot = response?.favoriteSpot;
      if (!updatedSpot || updatedSpot.spotKey !== spot.spotKey) {
        throw new Error("favorite_spot_response_invalid");
      }
      setDraft(
        normalizeFishingSetting(
          updatedSpot.habitatContext || emptyFishingSetting()
        )
      );
      onSaved?.(updatedSpot);
      if (typeof window !== "undefined") {
        window.dispatchEvent(
          new CustomEvent("favoriteSpotHabitatUpdated", {
            detail: { favoriteSpot: updatedSpot },
          })
        );
      }
      setNotice(t("fishing_today_setting_saved"));
    } catch (_error) {
      setError(t("fishing_today_setting_save_failed"));
    } finally {
      setIsSaving(false);
    }
  };

  const actions = (
    <div className="rp-favorite-setting__actions">
      <button
        type="button"
        className="rp-favorite-setting__save"
        disabled={!isDirty || isSaving}
        onClick={() => save(draft)}
      >
        {t("fishing_today_setting_save")}
      </button>
      {hasSavedSetting ? (
        <button
          type="button"
          className="rp-favorite-setting__remove"
          disabled={isSaving}
          onClick={() => save(emptyFishingSetting())}
        >
          {t("fishing_today_setting_remove_saved")}
        </button>
      ) : null}
      {notice ? <span role="status">{notice}</span> : null}
      {error ? <span role="alert">{error}</span> : null}
    </div>
  );

  return (
    <div className="rp-favorite-setting">
      <MarineFishingPlatform value={platform} required={false} disabled={isSaving} t={t}
        onChange={(value) => { setPlatform(value); setPlatformEdited(true); setNotice(""); setError(""); }} />
      {platform ? <button type="button" disabled={isSaving}
        onClick={() => { setPlatform(null); setPlatformEdited(true); }}>{t("marine_context_clear")}</button> : null}
      <SpeciesFishingSetting
        platform={platform}
        declaredHabitat={{
          habitatFeatures: savedSetting.habitatFeatures,
          accessContext: savedSetting.accessContext,
          source: hasSavedSetting ? "user_confirmed" : "unknown",
          scoringActive: false,
        }}
        idPrefix={idPrefix}
        isLoading={isSaving}
        onChange={(value) => {
          setDraft(normalizeFishingSetting(value));
          setNotice("");
          setError("");
        }}
        t={t}
        value={draft}
        waterbody="unknown"
      />
      {actions}
      {hasSavedSetting ? (
        <span className="rp-favorite-setting__saved">
          {t("fishing_today_setting_saved_for_spot")}
        </span>
      ) : null}
    </div>
  );
}
